import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/auth";
import { getSessionMessages } from "@/store";
import { Message } from "@/store/types";
import { logger } from "@/utils/logger";

/**
 * Return interface for the messages hook
 */
export interface UseMessagesReturn {
  /** Array of messages in the session */
  messages: Message[];
  /** Load messages from Firestore for a given session */
  loadFromStore: (sessionId: string) => Promise<void>;
  /** Append or update a message (handles streaming with same ID) */
  appendMessage: (message: Message) => void;
  /** Clear all messages */
  clearMessages: () => void;
  /** Whether messages are loading from Firestore */
  isLoading: boolean;
  /** Any error that occurred during loading */
  error: string | null;
}

/**
 * Hook for managing message state with support for loading from Firestore
 * and appending/updating messages (including streaming updates with same ID).
 *
 * This hook does NOT write to Firestore - it only reads from it and manages
 * local state. Messages can be updated multiple times with the same ID to
 * support streaming responses.
 *
 * @returns Messages state and operations
 */
export function useMessages(): UseMessagesReturn {
  const { user } = useAuth();

  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load messages from Firestore for a given session
   */
  const loadFromStore = useCallback(
    async (sessionId: string): Promise<void> => {
      if (!user) {
        const errorMsg = "Cannot load messages: user not authenticated";
        logger.error(errorMsg);
        setError(errorMsg);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const loadedMessages = await getSessionMessages(user.uid, sessionId);
        setMessages(loadedMessages);
        logger.info(`Loaded ${loadedMessages.length} messages from Firestore`);
      } catch (err) {
        logger.error("Failed to load session messages", err);
        setError("Failed to load conversation history");
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  /**
   * Append or update a message.
   * If a message with the same ID exists, it will be replaced (for streaming).
   * If the message ID is new, it will be appended to the end.
   */
  const appendMessage = useCallback((message: Message) => {
    setMessages((prev) => {
      const existingIndex = prev.findIndex((msg) => msg.id === message.id);

      if (existingIndex !== -1) {
        // Replace existing message (streaming update) — move to end so
        // the actively-updating bubble is always the last one shown.
        const updated = [...prev];
        updated.splice(existingIndex, 1);
        updated.push(message);
        return updated;
      } else {
        return [...prev, message];
      }
    });
  }, []);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    loadFromStore,
    appendMessage,
    clearMessages,
    isLoading,
    error
  };
}
